<template>
  <section>
    <EmptyData
      v-if="nodesAndTrails?.chapters?.length === 0 && !loader"
      :data="nodesAndTrails?.chapters?.length === 0 && !loader"
      :title="emptyState.title"
      :label="emptyState.description"
      :image="emptyState.image"
      imgSize="h-40 my-10"
    />
    <div
      id="learning-map"
      v-else
      :class="`w-full z-10  cursor-grab active:cursor-grabbing relative ${
        user.role === 'STUDENT' ? 'bg-h-primary-50' : 'bg-white'
      }`"
    >
      <Loader :loader="loader" />
      <div class="absolute right-4 top-4">
        <img src="@/assets/images/learning-map/tutorial.svg" alt="arraste" />
      </div>

      <MaterialModal
        v-if="configModal.showMaterials"
        :showMaterialsModal="configModal.showMaterials"
        :materials="materials"
        :learningObjective="learningObjective"
        :config="configModal"
        @onCloseMaterialsModal="closeMaterialsModal"
        @onChooseMaterial="chooseMaterial"
      />
      <div class="w-full h-[65vh] relative">
        <div
          ref="titleLabel"
          id="title-label"
          class="z-50 bg-white hidden justify-center items-center flex-col absolute rounded-md shadow-md p-2"
        >
          <p ref="titleP"></p>
        </div>
        <v-network-graph
          ref="graph"
          class="graph"
          :configs="configs"
          :nodes="nodes"
          :edges="edges"
          :layouts="layouts"
          :event-handlers="eventHandlers()"
        >
          <defs>
            <clipPath id="faceCircle" clipPathUnits="objectBoundingBox">
              <circle cx="0.5" cy="0.5" r="0.5" />
            </clipPath>
          </defs>

          <template #override-node="{ nodeId, ...slotProps }">
            <circle
              class="face-circle"
              :r="`${nodes[nodeId].width / 2}`"
              fill="#00000000"
              v-bind="slotProps"
            />
            <image
              :class="`face-picture ${
                nodes[nodeId].type != 'material'
                  ? 'cursor-default'
                  : 'cursor-pointer'
              }`"
              :x="nodes[nodeId].x"
              :y="nodes[nodeId].y"
              :width="nodes[nodeId].width"
              :height="nodes[nodeId].height"
              :xlink:href="`${nodes[nodeId].face}`"
              clip-path="url(#faceCircle)"
            />
            <circle
              class="face-circle"
              :r="`${nodes[nodeId].width / 2}`"
              fill="none"
              v-bind="slotProps"
            />
          </template>
        </v-network-graph>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { LearningObjective } from "@/models/learning-objective.model";
import { ConfigModal, Material } from "@/models/material.model";
import { getUserFromStorage } from "@/utils/user-store.util";
//@ts-ignore
import { last, take } from "rxjs";
import * as vNG from "v-network-graph";
import { Edges } from "v-network-graph";
import { defineComponent, reactive } from "vue";
import { LearningMapService } from "./learning-map.service";
import { nodePosition, buttonsStudent } from "./learning-map.util";
export default defineComponent({
  name: "learning-map",
  computed: {
    service(): LearningMapService {
      return new LearningMapService();
    },
    user(): any {
      return getUserFromStorage();
    },
  },
  emits: ["onLoadMaterialInModal"],

  data() {
    return {
      nodeSize: 40,
      loader: true,
      edges: {} as Edges,
      nodes: {} as any,
      layouts: reactive({
        nodes: {},
      }) as any,
      nodesAndTrails: [] as any,
      step: { x: 250, y: 150 },
      _menuButtons: [] as any,
      configs: vNG.defineConfigs({
        view: {
          minZoomLevel: 0.8,
          maxZoomLevel: 1,
          panEnabled: true,
          autoPanOnResize: true,
        },
        node: {
          draggable: false,
          normal: {
            type: "circle",
            radius: 32,
            width: 32,
            height: 32,
          },
          hover: {
            type: "circle",
            radius: 32,
            width: 32,
            height: 32,
          },
          label: {
            visible: true,
            fontSize: 12,
            lineHeight: 1.1,
            color: "#000000",
            margin: 15,
            direction: "south",
            background: {
              visible: true,
              color: "#F6FAFD",
              padding: {
                vertical: 0,
                horizontal: 4,
              },
              borderRadius: 2,
            },
          },
        },
        edge: {
          normal: { color: "gray", width: 1.2 },
          hover: { color: "gray", width: 1.2 },
          margin: 8,
          marker: {
            source: {
              type: "none",
            },
            target: {
              type: "arrow",
            },
          },
        },
      }),
      choosenNode: {} as any,
      currentEdge: 0 as number,
      edgeSource: "" as string,
      showMap: false as boolean,
      materials: [] as any[],
      learningObjective: {} as LearningObjective,
      currentNode: {} as any,
      configModal: new ConfigModal(),
      emptyState: {
        image: "empty-crawler.svg",
        title: this.$t("labels.generic", { pt: "Sem dados", en: "No data" }),
        description: this.$t("labels.generic", {
          pt: "Ainda não há dados suficientes",
          en: "No data available",
        }),
      },
    };
  },
  props: {
    userId: {
      type: String,
      default: "",
    },
    courseId: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "STUDENT",
    },
  },
  mounted() {
    this.firstPoint();
    this.getLearningMap();
    this.configModal.typeModalHeader = this.type;
    this.configs.node.label.background.color =
      this.user.role === "STUDENT" ? "#F6FAFD" : "#FFF";
  },
  unmounted() {
    nodePosition.xActual = null;
    nodePosition.yActual = null;
  },
  methods: {
    eventHandlers(): vNG.EventHandlers {
      const eventHandlers: vNG.EventHandlers = {
        "node:pointerover": ({ node, event }) => {
          if (node != "invisible") {
            this.nodes[node].face = this.nodes[node].faceHover;
          }
          setTimeout(() => {
            this.showTitleLabel(
              event.offsetX,
              event.offsetY,
              this.nodes[node].nameComplete
            );
          }, 300);
        },
        "node:pointerout": ({ node }) => {
          if (node != "invisible") {
            this.nodes[node].face = this.nodes[node].faceOut;
          }
          setTimeout(() => {
            this.hiddenTitleLabel();
          }, 300);
        },
        "node:click": ({ node }) => {
          if (this.nodes[node].type == "material") {
            this.loadModalMaterial(this.nodes[node]);
          }
        },
      };
      return eventHandlers;
    },
    firstPoint(): void {
      this.nodes["start"] = {
        name: "",
        nameComplete: "Início",
        type: "start",
        x: buttonsStudent["start"].x,
        y: buttonsStudent["start"].y,
        width: buttonsStudent["start"].width,
        height: buttonsStudent["start"].height,
        face: buttonsStudent["start"].icon,
        faceHover: buttonsStudent["start"].iconHover,
        faceOut: buttonsStudent["start"].icon,
      };
      this.layouts.nodes["start"] = {
        x: this.user.role === "STUDENT" ? -1000 : -400,
        y: -250,
      };
      this.nodes["invisible"] = {
        name: "",
        type: "chapter",
        face: "",
        x: 0,
        y: 0,
        width: buttonsStudent["chapter"].width,
        height: buttonsStudent["chapter"].height,
      };
      this.layouts.nodes["invisible"] = {
        x: window.innerWidth / 2 - 300,
        y: 200,
      };
      nodePosition.xStart =
        this.user.role === "STUDENT" ? nodePosition.xStart : -250;
      this.edgeSource = "start";
    },
    createPoints(): void {
      this.nodesAndTrails.chapters.forEach((chapter) => {
        chapter.type = "chapter";
        this.processChapter(chapter);
      });
      setTimeout(() => (this.loader = false), 100);
    },

    processChapter(chapter: any): void {
      this.createNode(chapter);
      this.positionNode(chapter);
      nodePosition.xActual = nodePosition.xStart;
      nodePosition.yActual = nodePosition.yActual + 150;
      if (this.edgeSource) {
        this.createEdge(this.edgeSource, chapter.id);
      }
      this.edgeSource = chapter.id;
      chapter.learningObjectives.forEach((learningObjective) => {
        learningObjective.learningObjective = learningObjective.name;
        learningObjective.chapter = chapter.name;
        learningObjective.type = "objective";
        this.processLearningObjective(learningObjective);
      });
    },

    processLearningObjective(learningObjective: any): void {
      this.createNode(learningObjective);
      this.positionNode(learningObjective);
      this.createEdge(this.edgeSource, learningObjective.id);
      this.edgeSource = learningObjective.id;
      nodePosition.xActual = nodePosition.xActual + 150;
      learningObjective.materials.forEach((material) => {
        material.learningObjective = learningObjective.name;
        material.idLearningObjective = learningObjective.id;
        material.chapter = learningObjective.chapter;
        material.materials = learningObjective.materials;
        material.type = "material";
        this.processMaterial(material);
      });
      this.edgeSource = learningObjective.id;
      nodePosition.xActual = nodePosition.xStart;
      nodePosition.yActual = nodePosition.yActual + 150;
    },

    processMaterial(material: any): void {
      this.createNode(material);
      this.positionNode(material);
      this.createEdge(this.edgeSource, material.id);
      this.edgeSource = material.id;
      nodePosition.xActual = nodePosition.xActual + 150;
    },
    createNode(node) {
      this.nodes[node.id] = {
        name: this.adjustName(node.name),
        id: node.id,
        nameComplete: node.name,
        type: node.type,
        face: buttonsStudent[node.type].icon,
        faceHover: buttonsStudent[node.type].iconHover,
        faceOut: buttonsStudent[node.type].icon,
        x: buttonsStudent[node.type].x,
        y: buttonsStudent[node.type].y,
        width: buttonsStudent[node.type].width,
        height: buttonsStudent[node.type].height,
        learningObjective:
          node.type === "chapter"
            ? ""
            : { name: node.learningObjective, id: node.idLearningObjective },
        chapter: node.type === "chapter" ? "" : node.chapter,
        materials:
          node.type != "chapter" || node.type != "objective"
            ? node.materials
            : [],
      };
    },
    createEdge(source: string, target: string): void {
      this.edges["edge" + this.currentEdge] = {
        source: source,
        target: target,
      };
      this.currentEdge = this.currentEdge + 1;
    },
    initializeNodePosition(): void {
      const width = window.innerWidth / 2 - 150;
      const height = window.innerHeight / 2 - 400;
      nodePosition.xStart = width * -1;
      nodePosition.yStart = height * -1;
    },
    positionNode(node): void {
      if (nodePosition.xActual == null) {
        nodePosition.xActual = nodePosition.xStart;
      }
      if (nodePosition.yActual == null) {
        nodePosition.yActual = nodePosition.yStart;
      }
      this.layouts.nodes[node.id] = {
        x: nodePosition.xActual,
        y: nodePosition.yActual,
      };
    },
    showTitleLabel(posX: number, posY: number, name: string): void {
      let titleLabel = this.$refs.titleLabel as HTMLElement;
      let titleP = this.$refs.titleP as HTMLElement;

      if (titleLabel !== null) {
        titleLabel.style.top = posY + 20 + "px";
        titleLabel.style.left = posX + "px";
        titleP.textContent = name;
        titleLabel.style.display = "flex";
      }
    },
    hiddenTitleLabel(): void {
      let titleLabel = this.$refs.titleLabel as HTMLElement;
      if (titleLabel !== null) {
        titleLabel.style.display = "none";
      }
    },

    adjustName(name: string): string {
      name = name.toLowerCase();
      if (name.length > 20) {
        name = name.substring(0, 20) + "...";
      }
      let words = name.split(" ");
      let capitalizedWords = words.map(function (word) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      });
      return capitalizedWords.join(" ");
    },
    getLearningMap(): void {
      this.service.learningMapStudent.subscribe({
        next: (response: any) => {
          this.nodesAndTrails = response;
          if (response.chapters) {
            this.createPoints();
          }
        },
      });
      this.service.getLearningMapStudent(this.courseId, this.userId);
    },
    loadModalMaterial(node: any): void {
      this.configModal.maxIndexMaterial = node.materials.length - 1;
      this.learningObjective = node.learningObjective;
      this.configModal.currentIndex = node.materials.findIndex(
        (obj) => obj.id === node.id
      );
      this.currentNode = node;
      this.chooseMaterial(
        this.learningObjective,
        this.configModal.currentIndex
      );
    },
    getMaterialsById(
      learningObjectiveId: string,
      materialId: string
    ): Promise<void> {
      return new Promise<void>((resolve, reject) => {
        this.service.material.pipe(take(2), last()).subscribe({
          next: (response) => {
            this.materials[0] = response;
            this.materials[0].info =
              this.currentNode.materials[this.configModal.currentIndex];
            resolve();
          },
          error: (e) => {
            reject(e);
          },
        });
        this.service.getMaterialsById(learningObjectiveId, materialId);
      });
    },
    closeMaterialsModal(): void {
      this.configModal.showMaterials = false;
    },
    chooseMaterial(learningObjective: LearningObjective, index: number): void {
      this.configModal.currentIndex = index;
      let materialId =
        this.currentNode.materials[this.configModal.currentIndex].id;
      this.getMaterialsById(learningObjective.id, materialId).then(() => {
        this.configModal.showMaterials = true;
      });
    },
  },
});
</script>
